import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import AnswerPerformanceFilters from 'components/answer_performance/answer_performance_filters';
import AssignWindowLocation from 'actions/lib/assign_window_location';
import connect from 'components/lib/connect';
import InteractiveTable from 'components/common/interactive_table';
import NavigateTo from 'actions/current_location/navigate_to';
import { NO_ANSWER_REFERENCED } from 'scripts/domain/models/answer_performance';
import { Number } from 'components/common/number';
import RecommendedAnswers from 'models/location/recommended_answers';
import RecommendedAnswersIcon from 'components/common/icons/recommended_answers_icon';
import Tooltip from 'components/common/tooltip';
import theme from 'scripts/presentation/themes/default';

export function AnswerPerformanceTable({
  arePerformanceCellsClickable,
  data,
  initialSort,
  isLoading,
  navigateToSidekickConversationList,
  onOpenRecommendedAnswers,
  onSort,
}) {
  const showRecommendedAnswers = data?.some(row => row.recommendedAnswersCount);
  const transformedData = data?.map(row => ({
    id: row.id,
    recommendedAnswersIcon: showRecommendedAnswers && (
      <IconCell>
        {row.recommendedAnswersCount && (
          <Tooltip data-aid="recommendedAnswersCount-tooltip" message={`${row.recommendedAnswersCount}`}>
            <RecommendedAnswersIcon width="16px" />
          </Tooltip>
        )}
      </IconCell>
    ),
    name: <NameCell>{row.name}</NameCell>,
    references: <Number value={row.references} />,
    handoffs: <Number value={row.handoffs} />,
    resolved: <Number value={row.resolved} />,
    resolutionRate: (
      <span>
        <Number value={row.resolutionRate} />%
      </span>
    ),
  }));

  const rowHasRecommendedAnswers = row => {
    return data.find(d => d.id === row.id)?.recommendedAnswersCount > 0;
  };

  const navigateToRecommendedAnswers = row => {
    onOpenRecommendedAnswers({ referencedAnswerId: row.id });
  };

  const columns = [
    ...(showRecommendedAnswers
      ? [
          {
            header: '',
            accessor: 'recommendedAnswersIcon',
            align: 'center',
            width: '24px',
            sortValue: item => data.find(d => d.id === item.id).recommendedAnswersCount ?? -1,
            isCellClickable: rowHasRecommendedAnswers,
            onCellClick: navigateToRecommendedAnswers,
          },
        ]
      : []),
    {
      header: 'Answer',
      accessor: 'name',
      align: 'left',
      width: '1fr',
      sortValue: item => data.find(d => d.id === item.id).name,
      isCellClickable: rowHasRecommendedAnswers,
      onCellClick: navigateToRecommendedAnswers,
    },
    {
      header: 'Count',
      accessor: 'references',
      align: 'right',
      padding: `${theme.spacing.medium} ${theme.spacing.large}`,
      width: 'min-content',
      sortValue: item => data.find(d => d.id === item.id).references,
      infoText: 'The number of times an answer was referenced.',
      sortDescendingFirst: true,
      isCellClickable: () => arePerformanceCellsClickable,
      onCellClick: row => navigateToSidekickConversationList(row, 'references'),
    },
    {
      header: 'Handed off',
      accessor: 'handoffs',
      align: 'right',
      padding: `${theme.spacing.medium} ${theme.spacing.large}`,
      width: 'min-content',
      sortValue: item => data.find(d => d.id === item.id).handoffs,
      sortDescendingFirst: true,
      isCellClickable: () => arePerformanceCellsClickable,
      onCellClick: row => navigateToSidekickConversationList(row, 'handoffs'),
    },
    {
      header: 'Resolved',
      accessor: 'resolved',
      align: 'right',
      padding: `${theme.spacing.medium} ${theme.spacing.large}`,
      width: 'min-content',
      sortValue: item => data.find(d => d.id === item.id).resolved,
      sortDescendingFirst: true,
      isCellClickable: () => arePerformanceCellsClickable,
      onCellClick: row => navigateToSidekickConversationList(row, 'resolved'),
    },
    {
      header: 'Resolution rate',
      accessor: 'resolutionRate',
      align: 'right',
      padding: `${theme.spacing.medium} ${theme.spacing.large}`,
      width: 'min-content',
      sortValue: item => data.find(d => d.id === item.id).resolutionRate,
      sortDescendingFirst: true,
    },
  ];

  const reorderRows = tableData => {
    return [
      ...tableData.filter(row => row.id === NO_ANSWER_REFERENCED),
      ...tableData.filter(row => row.id !== NO_ANSWER_REFERENCED),
    ];
  };

  return (
    <InteractiveTable
      columns={columns}
      controlComponents={<AnswerPerformanceFilters />}
      data={transformedData || []}
      enableSearch
      enableSorting
      initialSort={initialSort?.key ? initialSort : { key: 'references' }}
      isLoading={isLoading}
      onSortConfigUpdate={onSort}
      reorderRows={reorderRows}
      rowsPerPage={25}
    />
  );
}

AnswerPerformanceTable.propTypes = {
  arePerformanceCellsClickable: PropTypes.bool.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      references: PropTypes.number.isRequired,
      handoffs: PropTypes.number.isRequired,
      resolved: PropTypes.number.isRequired,
      resolutionRate: PropTypes.number.isRequired,
      recommendedAnswersCount: PropTypes.number,
    })
  ),
  isLoading: PropTypes.bool,
  initialSort: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.oneOf(['asc', 'desc']),
  }),
  onOpenRecommendedAnswers: PropTypes.func,
  navigateToSidekickConversationList: PropTypes.func,
  onSort: PropTypes.func,
};

const NameCell = styled.div`
  align-items: center;
  display: flex;
  gap: ${p => p.theme.spacing.small};
`;

const IconCell = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  line-height: 0.5;
  padding-top: ${p => p.theme.spacing.small};
`;

function mapStateToProps({ getProvider, isFeatureEnabled }) {
  const arePerformanceCellsClickable = isFeatureEnabled('viewAnswerPerformance');
  const answerPerformancesProvider = getProvider('answerPerformances');
  const data = answerPerformancesProvider.findAll();

  const recommendedAnswersCountsProvider = getProvider('recommendedAnswersCounts');
  const recommendedAnswerCounts = recommendedAnswersCountsProvider.findAll();
  const combinedData = data.map(row => {
    const recommendedAnswerCount = recommendedAnswerCounts.find(count => count.id === row.id)?.count;
    return {
      ...row,
      recommendedAnswersCount: recommendedAnswerCount,
    };
  });

  const location = getProvider('currentLocation').get();

  return {
    arePerformanceCellsClickable,
    data: combinedData,
    isLoading: answerPerformancesProvider.isLoading() || recommendedAnswersCountsProvider.isLoading(),
    initialSort: location.sort,
  };
}

function mapExecuteToProps(executeAction, { currentLocation }) {
  return {
    onOpenRecommendedAnswers: ({ referencedAnswerId }) => {
      executeAction(NavigateTo, RecommendedAnswers.create({ referencedAnswerId }));
    },
    navigateToSidekickConversationList: ({ id: answerId }, columnType) => {
      const params = {
        'f.ss': 'ENDED',
        'f.aid': answerId,
        ...(columnType === 'handoffs' && { 'f.as': 'HANDED_OFF' }),
        ...(columnType === 'resolved' && { 'f.as': 'RESOLVED' }),
      };

      return executeAction(
        AssignWindowLocation,
        `/sidekick/communication-sessions?${new URLSearchParams(params).toString()}`
      );
    },
    onSort: sortConfig => {
      executeAction(NavigateTo, currentLocation.deriveNew({ sort: sortConfig }));
    },
  };
}

const AnswerPerformanceTableContainer = connect(mapStateToProps, mapExecuteToProps)(AnswerPerformanceTable);

export default AnswerPerformanceTableContainer;
